import React, { useEffect, useState, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import DOMPurify from "dompurify";
import API_BASE from "../config";

// Render admin-authored rich text (proposal Nature of Work / Description / Deliverables)
// as sanitized HTML. The admin editor stores clean semantic HTML; DOMPurify is defense
// in depth before dangerouslySetInnerHTML.
const RichText = ({ html }) => (
    <div
        className="rte-content"
        style={{ fontSize: 14, color: "#334155", lineHeight: 1.5 }}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html || "") }}
    />
);

// Minimal, fully-isolated client-facing portal (rendered only for the Cognito
// 'client' group). Proposals → proposal detail (project info, attachments, discussion,
// quotes) → quote view with PDF, comments, and Accept / Reject / Request Revision. An
// approval captures a drawn e-signature + typed name + consent.
const ClientPortal = ({ userData, onLogout }) => {
    const [me, setMe] = useState(null);
    const [proposals, setProposals] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [payingId, setPayingId] = useState(null);
    const [notice, setNotice] = useState("");
    const [selected, setSelected] = useState(null);   // proposal detail
    const [quote, setQuote] = useState(null);          // quote view
    const [commentText, setCommentText] = useState("");
    const [decision, setDecision] = useState(null);    // 'approved'|'rejected'|'revision_requested'|null
    const [signerName, setSignerName] = useState("");
    const [consent, setConsent] = useState(false);
    const [decisionComment, setDecisionComment] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const sigRef = useRef(null);

    const loadList = async () => {
        try {
            const meRes = await fetch(`${API_BASE}/client-portal/me`, { credentials: "include" });
            if (!meRes.ok) throw new Error("Could not load your client profile.");
            setMe(await meRes.json());
            const pRes = await fetch(`${API_BASE}/client-portal/proposals`, { credentials: "include" });
            if (pRes.ok) setProposals((await pRes.json()).proposals || []);
            const iRes = await fetch(`${API_BASE}/client-portal/invoices`, { credentials: "include" });
            if (iRes.ok) setInvoices((await iRes.json()).invoices || []);
        } catch (e) { setError(e.message); }
    };
    useEffect(() => { loadList(); }, []);

    // Returning from a Stripe payment (success_url adds ?paid=1). Show a note; the
    // webhook marks the invoice paid, so a reload reflects the new status.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("paid") === "1") {
            setNotice("Payment received — thank you! Your invoice status will update shortly.");
            window.history.replaceState({}, "", window.location.pathname);
        }
    }, []);

    const payInvoice = async (id) => {
        setPayingId(id); setError("");
        try {
            const res = await fetch(`${API_BASE}/client-portal/invoices/${id}/pay`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: "{}" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Could not start payment.");
            if (data.url) window.location.href = data.url;
        } catch (e) { setError(e.message); setPayingId(null); }
    };

    const downloadInvoice = async (id) => {
        const res = await fetch(`${API_BASE}/client-portal/invoices/${id}/pdf`, { credentials: "include" });
        const data = await res.json();
        if (data.url) window.open(data.url, "_blank", "noopener");
    };

    const openProposal = async (id) => {
        setError(""); setQuote(null);
        const res = await fetch(`${API_BASE}/client-portal/proposals/${id}`, { credentials: "include" });
        if (!res.ok) { setError("Could not load that proposal."); return; }
        setSelected(await res.json());
        setCommentText("");
    };

    const openQuote = async (id) => {
        setError("");
        const res = await fetch(`${API_BASE}/client-portal/quotes/${id}`, { credentials: "include" });
        if (!res.ok) { setError("Could not load that quote."); return; }
        setQuote(await res.json());
    };

    const addProposalComment = async () => {
        if (!commentText.trim()) return;
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE}/client-portal/proposals/${selected.proposal.id}/comments`, {
                method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
                body: JSON.stringify({ body: commentText.trim() }),
            });
            if (!res.ok) throw new Error("Failed to post comment.");
            setCommentText("");
            await openProposal(selected.proposal.id);
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    const downloadProposalAttachment = async (docId) => {
        const res = await fetch(`${API_BASE}/client-portal/proposals/${selected.proposal.id}/attachments/${docId}/download`, { credentials: "include" });
        const data = await res.json();
        if (data.url) window.open(data.url, "_blank", "noopener");
    };

    const downloadProposalPdf = async () => {
        setBusy(true); setError("");
        try {
            const res = await fetch(`${API_BASE}/client-portal/proposals/${selected.proposal.id}/pdf`, { method: "POST", credentials: "include" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed to generate PDF.");
            if (data.url) window.open(data.url, "_blank", "noopener");
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    const startDecision = (d) => { setDecision(d); setSignerName(me?.client?.primary_contact_name || ""); setConsent(false); setDecisionComment(""); };

    const submitDecision = async () => {
        setBusy(true); setError("");
        try {
            const body = { decision, comment: decisionComment };
            if (decision === "approved") {
                if (!signerName.trim()) throw new Error("Please type your full name.");
                if (!consent) throw new Error("Please check the consent box to sign.");
                if (!sigRef.current || sigRef.current.isEmpty()) throw new Error("Please draw your signature.");
                let dataUrl;
                try { dataUrl = sigRef.current.getTrimmedCanvas().toDataURL("image/png"); }
                catch (_) { dataUrl = sigRef.current.getCanvas().toDataURL("image/png"); }
                body.signer_name = signerName.trim();
                body.consent = true;
                body.signature = dataUrl;
            }
            const res = await fetch(`${API_BASE}/client-portal/quotes/${quote.quote.id}/decision`, {
                method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
                body: JSON.stringify(body),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed to record decision.");
            setDecision(null);
            await openQuote(quote.quote.id);
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20, boxShadow: "0 10px 30px rgba(15,23,42,0.06)" };
    const btn = (bg) => ({ padding: "10px 16px", background: bg, color: "#fff", border: "none", borderRadius: 10, fontWeight: "bold", cursor: "pointer" });
    const money = (n, c) => `${c || "USD"} ${Number(n || 0).toFixed(2)}`;

    return (
        <div style={{ minHeight: "100vh", background: "#f1f5f9" }}>
            <style>{`
              .rte-content ul { list-style: disc; padding-left: 1.4em; margin: 6px 0; }
              .rte-content ol { list-style: decimal; padding-left: 1.4em; margin: 6px 0; }
              .rte-content li { margin: 2px 0; }
              .rte-content h1 { font-size: 20px; margin: 10px 0 6px; }
              .rte-content h2 { font-size: 17px; margin: 10px 0 6px; }
              .rte-content h3 { font-size: 15px; margin: 8px 0 4px; }
              .rte-content p { margin: 6px 0; }
              .rte-content u { text-decoration: underline; }
            `}</style>
            <div style={{ background: "linear-gradient(180deg,#232e47,#111733)", color: "#e5e7eb", padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <strong style={{ color: "#cbd5f5", cursor: "pointer" }} onClick={() => { setQuote(null); setSelected(null); }}>CloudGenomics · Client Portal</strong>
                <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                    <span style={{ fontSize: 13 }}>{me?.email || userData?.email || ""}</span>
                    <button onClick={onLogout} style={{ padding: "8px 14px", background: "#ef4444", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>Logout</button>
                </div>
            </div>

            <div style={{ maxWidth: 960, margin: "0 auto", padding: "32px 24px" }}>
                {error && <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#7f1d1d", padding: "12px 16px", borderRadius: 8, marginBottom: 16 }}>{error}</div>}
                {notice && <div style={{ background: "#ecfdf5", border: "1px solid #6ee7b7", color: "#065f46", padding: "12px 16px", borderRadius: 8, marginBottom: 16 }}>{notice}</div>}

                {/* QUOTE VIEW */}
                {quote ? (
                    <>
                        <button onClick={() => setQuote(null)} style={{ background: "#e2e8f0", border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer", marginBottom: 12 }}>‹ Back to proposal</button>
                        <div style={card}>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <h2 style={{ marginTop: 0 }}>Quote {quote.quote.quote_number} <span style={{ fontSize: 14, color: "#64748b" }}>v{quote.version.version}</span></h2>
                                <span style={{ fontWeight: 600, color: "#475569" }}>{quote.quote.status}</span>
                            </div>
                            {quote.pdfUrl && <a href={quote.pdfUrl} target="_blank" rel="noopener noreferrer">Download PDF</a>}
                            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 16 }}>
                                <thead><tr>
                                    <th style={{ textAlign: "left", background: "#273d3b", color: "#fff", padding: 8 }}>Description</th>
                                    <th style={{ textAlign: "right", background: "#273d3b", color: "#fff", padding: 8 }}>Qty</th>
                                    <th style={{ textAlign: "left", background: "#273d3b", color: "#fff", padding: 8 }}>Unit</th>
                                    <th style={{ textAlign: "right", background: "#273d3b", color: "#fff", padding: 8 }}>Unit Price</th>
                                    <th style={{ textAlign: "right", background: "#273d3b", color: "#fff", padding: 8 }}>Amount</th>
                                </tr></thead>
                                <tbody>
                                    {(quote.items || []).map((it, i) => (
                                        <tr key={i}>
                                            <td style={{ padding: 8, borderBottom: "1px solid #e2e8f0" }}>
                                                <div style={{ fontWeight: 600 }}>{it.title}</div>
                                                {it.description ? <div style={{ color: "#475569", fontSize: 13, whiteSpace: "pre-wrap" }}>{it.description}</div> : null}
                                            </td>
                                            <td style={{ padding: 8, borderBottom: "1px solid #e2e8f0", textAlign: "right" }}>{Number(it.quantity)}</td>
                                            <td style={{ padding: 8, borderBottom: "1px solid #e2e8f0" }}>{it.unit}</td>
                                            <td style={{ padding: 8, borderBottom: "1px solid #e2e8f0", textAlign: "right" }}>{Number(it.unit_price).toFixed(2)}</td>
                                            <td style={{ padding: 8, borderBottom: "1px solid #e2e8f0", textAlign: "right" }}>{Number(it.amount).toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div style={{ width: 280, marginLeft: "auto", marginTop: 12, fontSize: 14 }}>
                                <div style={{ display: "flex", justifyContent: "space-between" }}><span>Subtotal</span><span>{money(quote.version.subtotal, quote.version.currency)}</span></div>
                                <div style={{ display: "flex", justifyContent: "space-between" }}><span>Discount</span><span>-{money(quote.version.discount, quote.version.currency)}</span></div>
                                <div style={{ display: "flex", justifyContent: "space-between" }}><span>Tax ({Number(quote.version.tax_rate)}%)</span><span>{money(quote.version.tax, quote.version.currency)}</span></div>
                                <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", borderTop: "2px solid #213a22", marginTop: 4, paddingTop: 4 }}><span>Total</span><span>{money(quote.version.total, quote.version.currency)}</span></div>
                            </div>
                            {quote.version.terms && <div style={{ marginTop: 16, whiteSpace: "pre-wrap", fontSize: 13, color: "#334155" }}><strong>Terms &amp; Conditions</strong>{"\n"}{quote.version.terms}</div>}

                            {quote.approval ? (
                                <div style={{ marginTop: 20, padding: "12px 16px", borderRadius: 8, background: quote.approval.decision === "approved" ? "#ecfdf5" : "#fef2f2", border: "1px solid #e2e8f0" }}>
                                    This quote was <strong>{String(quote.approval.decision).replace(/_/g, " ")}</strong>{quote.approval.signer_name ? ` by ${quote.approval.signer_name}` : ""} on {new Date(quote.approval.signed_at).toLocaleString()}.
                                </div>
                            ) : quote.quote.status === "sent" ? (
                                <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
                                    <button style={btn("#16a34a")} onClick={() => startDecision("approved")}>Accept &amp; Sign</button>
                                    <button style={btn("#dc2626")} onClick={() => startDecision("rejected")}>Reject</button>
                                    <button style={btn("#d97706")} onClick={() => startDecision("revision_requested")}>Request Revision</button>
                                </div>
                            ) : null}
                        </div>
                    </>
                ) : !selected ? (
                    /* PROPOSALS LIST */
                    <>
                        <h1 style={{ color: "#0f172a" }}>Welcome{me?.client?.client_name ? `, ${me.client.client_name}` : ""}</h1>
                        <p style={{ color: "#475569" }}>Your proposals and quotes from CloudGenomics.</p>
                        <div style={{ ...card, marginTop: 16 }}>
                            <h3 style={{ marginTop: 0 }}>Proposals</h3>
                            {proposals.length ? proposals.map((p) => (
                                <div key={p.id} onClick={() => openProposal(p.id)} style={{ display: "flex", justifyContent: "space-between", padding: "12px 8px", borderTop: "1px solid #eef2f7", cursor: "pointer" }}>
                                    <span style={{ fontWeight: 600 }}>{p.name}</span>
                                    <span style={{ color: "#64748b" }}>{p.status}{p.deadline ? ` · due ${new Date(p.deadline).toLocaleDateString()}` : ""}</span>
                                </div>
                            )) : <p style={{ color: "#64748b" }}>No proposals yet. CloudGenomics will share proposals and quotes with you here.</p>}
                        </div>

                        <div style={{ ...card, marginTop: 16 }}>
                            <h3 style={{ marginTop: 0 }}>Invoices</h3>
                            {invoices.length ? (
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead><tr>
                                        <th style={{ textAlign: "left", padding: 8, borderBottom: "2px solid #e2e8f0", color: "#64748b" }}>Invoice</th>
                                        <th style={{ textAlign: "right", padding: 8, borderBottom: "2px solid #e2e8f0", color: "#64748b" }}>Amount</th>
                                        <th style={{ textAlign: "left", padding: 8, borderBottom: "2px solid #e2e8f0", color: "#64748b" }}>Due</th>
                                        <th style={{ textAlign: "left", padding: 8, borderBottom: "2px solid #e2e8f0", color: "#64748b" }}>Status</th>
                                        <th style={{ padding: 8, borderBottom: "2px solid #e2e8f0" }}></th>
                                    </tr></thead>
                                    <tbody>
                                        {invoices.map((inv) => (
                                            <tr key={inv.id}>
                                                <td style={{ padding: 8, borderBottom: "1px solid #eef2f7", fontWeight: 600 }}>{inv.invoice_number}</td>
                                                <td style={{ padding: 8, borderBottom: "1px solid #eef2f7", textAlign: "right" }}>{money(inv.total, inv.currency)}</td>
                                                <td style={{ padding: 8, borderBottom: "1px solid #eef2f7" }}>{inv.due_date ? new Date(inv.due_date).toLocaleDateString() : "—"}</td>
                                                <td style={{ padding: 8, borderBottom: "1px solid #eef2f7", textTransform: "capitalize", color: inv.status === "paid" ? "#16a34a" : "#475569" }}>{String(inv.status).replace(/_/g, " ")}</td>
                                                <td style={{ padding: 8, borderBottom: "1px solid #eef2f7", textAlign: "right", whiteSpace: "nowrap" }}>
                                                    <button onClick={() => downloadInvoice(inv.id)} style={{ background: "#e2e8f0", border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer", marginRight: 8 }}>PDF</button>
                                                    {inv.status !== "paid" && inv.status !== "void" && (
                                                        <button onClick={() => payInvoice(inv.id)} disabled={payingId === inv.id} style={{ ...btn("#2563eb"), padding: "6px 14px" }}>{payingId === inv.id ? "Redirecting…" : "Pay now"}</button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : <p style={{ color: "#64748b" }}>No invoices yet.</p>}
                        </div>
                    </>
                ) : (
                    /* PROPOSAL DETAIL */
                    <>
                        <button onClick={() => setSelected(null)} style={{ background: "#e2e8f0", border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer", marginBottom: 12 }}>‹ Back to proposals</button>
                        <div style={card}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                                <h2 style={{ marginTop: 0 }}>{selected.proposal.name}</h2>
                                <button onClick={downloadProposalPdf} disabled={busy} style={btn("#213a22")}>{busy ? "Preparing…" : "Download PDF"}</button>
                            </div>
                            <p style={{ color: "#64748b", marginTop: 0 }}>Status: {selected.proposal.status}{selected.proposal.deadline ? ` · Deadline: ${new Date(selected.proposal.deadline).toLocaleDateString()}` : ""}</p>
                            {selected.proposal.nature_of_work && (<div style={{ marginTop: 8 }}><strong>Nature of work</strong><RichText html={selected.proposal.nature_of_work} /></div>)}
                            {selected.proposal.description && (<div style={{ marginTop: 8 }}><strong>Description</strong><RichText html={selected.proposal.description} /></div>)}
                            {selected.proposal.deliverables && (<div style={{ marginTop: 8 }}><strong>Deliverables</strong><RichText html={selected.proposal.deliverables} /></div>)}

                            <h4>Quotes</h4>
                            {(selected.quotes || []).length ? selected.quotes.map((qq) => (
                                <div key={qq.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderTop: "1px solid #eef2f7" }}>
                                    <span>{qq.quote_number} · <span style={{ color: "#64748b" }}>{qq.status}</span> · {money(qq.total, qq.currency)}</span>
                                    <button style={{ ...btn("#2563eb"), padding: "6px 12px" }} onClick={() => openQuote(qq.id)}>View / Respond</button>
                                </div>
                            )) : <div style={{ color: "#64748b" }}>No quotes yet.</div>}

                            <h4 style={{ marginTop: 20 }}>Attachments</h4>
                            {(selected.attachments || []).length ? selected.attachments.map((d) => (
                                <div key={d.id}><a href="#!" onClick={(e) => { e.preventDefault(); downloadProposalAttachment(d.id); }}>{d.filename}</a></div>
                            )) : <div style={{ color: "#64748b" }}>No attachments.</div>}

                            <h4 style={{ marginTop: 20 }}>Discussion</h4>
                            <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8, padding: 10, marginBottom: 8 }}>
                                {(selected.comments || []).map((c) => (
                                    <div key={c.id} style={{ marginBottom: 10 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: c.author_type === "client" ? "#b45309" : "#2563eb" }}>
                                            {c.author_type === "client" ? "You" : "CloudGenomics"} · {new Date(c.created_at).toLocaleString()}
                                        </div>
                                        <div>{c.body}</div>
                                    </div>
                                ))}
                                {!(selected.comments || []).length && <div style={{ color: "#64748b" }}>No messages yet.</div>}
                            </div>
                            <textarea value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Write a message to CloudGenomics…"
                                style={{ width: "100%", minHeight: 60, padding: 10, border: "1px solid #cbd5e1", borderRadius: 8, boxSizing: "border-box" }} />
                            <button onClick={addProposalComment} disabled={busy} style={{ ...btn("#2563eb"), marginTop: 8 }}>{busy ? "Posting…" : "Post message"}</button>
                        </div>
                    </>
                )}
            </div>

            {/* DECISION MODAL */}
            {decision && (
                <div onClick={() => !busy && setDecision(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, padding: 24, width: 460, maxWidth: "94%" }}>
                        <h3 style={{ marginTop: 0, color: "#213a22" }}>
                            {decision === "approved" ? "Accept & Sign Quote" : decision === "rejected" ? "Reject Quote" : "Request Revision"}
                        </h3>
                        {decision === "approved" ? (
                            <>
                                <p style={{ fontSize: 13, color: "#475569" }}>By signing you accept quote {quote.quote.quote_number} (v{quote.version.version}), forming a binding agreement.</p>
                                <label>Full name</label>
                                <input value={signerName} onChange={(e) => setSignerName(e.target.value)} style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6, marginBottom: 8, boxSizing: "border-box" }} />
                                <label>Signature</label>
                                <div style={{ border: "1px solid #cbd5e1", borderRadius: 6, background: "#fff" }}>
                                    <SignatureCanvas ref={sigRef} penColor="#0f172a" canvasProps={{ width: 410, height: 140 }} />
                                </div>
                                <button onClick={() => sigRef.current && sigRef.current.clear()} style={{ background: "#e2e8f0", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", margin: "6px 0" }}>Clear</button>
                                <label style={{ display: "block", fontSize: 13, marginTop: 4 }}>
                                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} /> I consent to electronically sign and agree to be legally bound.
                                </label>
                            </>
                        ) : (
                            <>
                                <label>Comment{decision === "revision_requested" ? " (what should change?)" : " (optional)"}</label>
                                <textarea value={decisionComment} onChange={(e) => setDecisionComment(e.target.value)} style={{ width: "100%", minHeight: 80, padding: 8, border: "1px solid #cbd5e1", borderRadius: 6, boxSizing: "border-box" }} />
                            </>
                        )}
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
                            <button onClick={() => setDecision(null)} disabled={busy} style={{ background: "#e2e8f0", border: "none", borderRadius: 8, padding: "8px 16px", cursor: "pointer" }}>Cancel</button>
                            <button onClick={submitDecision} disabled={busy} style={btn(decision === "approved" ? "#16a34a" : decision === "rejected" ? "#dc2626" : "#d97706")}>
                                {busy ? "Submitting…" : decision === "approved" ? "Sign & Accept" : decision === "rejected" ? "Reject" : "Request Revision"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientPortal;
